// Clearing up after a fight (issue 812): when the encounter ends and its
// tokens are still on the board, the DM is asked once which to take off —
// every token linked to a combatant, a player's own starting unticked — and
// the ticked ones leave in ONE removal, so the single undelete brings them
// all back. "Keep them" leaves the board as it is; the Combat menu and the
// tracker offer the same question afterwards.
import { useEffect, useRef, useState } from 'react';
import { emptyEncounter, type DmObject, type Encounter } from '@dragons-table/vtt-shared';
import { Modal } from '../shell/Dialogs';
import { useTableState } from '../table/TableState';
import { useMayAct } from '../table/mayAct';
import { useRemoveObjects } from '../table/strandedCombatants';
import { boardBus } from './bus';
import { BTN_MD } from '../shell/ui';

export interface Leftover { id: string; name: string; theirs: boolean }

/** The tokens a fight leaves on the board: linked to a combatant and still there, in the order's order. A player's own starts unticked. */
export function fightLeftovers(enc: Pick<Encounter, 'combatants'>, objects: Record<string, Pick<DmObject, 'id' | 'name' | 'pc' | 'owners'>>): Leftover[] {
  const seen = new Set<string>();
  const out: Leftover[] = [];
  for (const c of enc.combatants) {
    for (const id of c.tokens) {
      const o = objects[id];
      if (!o || seen.has(id)) continue;
      seen.add(id);
      out.push({ id, name: o.name, theirs: o.pc || o.owners.length > 0 });
    }
  }
  return out;
}

const btn = BTN_MD;
const danger = 'rounded bg-red-800 px-3 py-1 text-sm font-medium text-red-50 hover:bg-red-700 disabled:opacity-50';

export function ClearFightGate() {
  const { store, role, act, viewingAs, scene } = useTableState();
  // The fight's combatants these leave tokenless are offered out of the turn order on the notice (issue 1537).
  const remove = useRemoveObjects();
  const [asking, setAsking] = useState<Leftover[] | null>(null);
  const [ticked, setTicked] = useState<Set<string>>(new Set());
  // The fight's templates go with its tokens (the 2026-10-15 session pass): a resolved burst stayed lit on the board
  // after every goblin was cleared, until the View menu's Clear every area. Ticked to start, when the DM may clear them.
  const [areasToo, setAreasToo] = useState(true);
  const mayClearAreas = useMayAct()('area.clearAll');
  const mayTokens = useMayAct()('object.remove'); // clearing the fight's leftovers is `tokens`' (issues 1159, 1160)
  const isDm = role === 'dm' && !viewingAs && mayTokens;
  const enc = (store.state.encounter ?? emptyEncounter()) as Encounter;
  const running = enc.running;
  const was = useRef(isDm && running);
  // Which scene the reading belongs to (issue 834): moving to another scene is not the fight ending.
  const onScene = useRef(scene?.id ?? null);
  const leftoversRef = useRef<() => Leftover[]>(() => []);
  leftoversRef.current = () => fightLeftovers(enc, store.state.objects as Record<string, DmObject>);

  const ask = (list: Leftover[]) => {
    if (list.length === 0) return;
    setTicked(new Set(list.filter((l) => !l.theirs).map((l) => l.id)));
    setAreasToo(true);
    setAsking(list);
  };

  // The fight ended — by this DM, in either window, or while they were away: asked once, here.
  // Looking through a player's eyes is not the fight ending: the DM's own reading is what is watched.
  useEffect(() => {
    if (!isDm) return;
    const here = scene?.id ?? null;
    if (onScene.current !== here) { onScene.current = here; was.current = running; return; }
    if (was.current && !running) ask(leftoversRef.current());
    was.current = running;
  }, [running, isDm, scene?.id]);
  useEffect(() => boardBus.onClearFight(() => { if (isDm) ask(leftoversRef.current()); }), [isDm]);

  // What is still on the board, by its name now: a token removed while the question is open (the DM's other window,
  // a Remove in Properties) leaves the list, and the question closes when none is left (2026-10-05).
  const objects = store.state.objects as Record<string, DmObject>;
  const standing = asking ? asking.filter((l) => objects[l.id]).map((l) => ({ ...l, name: objects[l.id]!.name })) : [];
  useEffect(() => { if (asking && standing.length === 0) setAsking(null); }, [asking, standing.length]);

  if (!asking || standing.length === 0) return null;
  const close = () => setAsking(null);
  const toggle = (id: string) => setTicked((cur) => { const next = new Set(cur); if (next.has(id)) next.delete(id); else next.add(id); return next; });
  const n = standing.filter((l) => ticked.has(l.id)).length;
  const areaCount = mayClearAreas ? Object.keys(store.state.areas ?? {}).length : 0;
  const clearAreas = areaCount > 0 && areasToo;
  return (
    <Modal title="Clear the fight's tokens?" onClose={close} testId="clear-fight">
      <div className="space-y-3 text-sm text-stone-300">
        <p>The encounter has ended. Tick the tokens to take off the board; their pictures stay in the library, and one undelete brings them all back until your next action.</p>
        <ul className="max-h-64 space-y-1 overflow-y-auto" aria-label="Tokens the fight left">
          {standing.map((l) => (
            <li key={l.id}>
              <label className="flex items-center gap-2">
                <input type="checkbox" checked={ticked.has(l.id)} onChange={() => toggle(l.id)} />
                <span>{l.name}</span>
                {l.theirs && <span className="text-xs text-stone-500">a player's</span>}
              </label>
            </li>
          ))}
        </ul>
        {areaCount > 0 && (
          <label className="flex items-center gap-2">
            <input type="checkbox" checked={areasToo} onChange={(e) => setAreasToo(e.target.checked)} />
            <span>{areaCount === 1 ? 'Also clear the area template on the board' : `Also clear the ${areaCount} area templates on the board`}</span>
          </label>
        )}
        <div className="flex justify-end gap-2">
          <button type="button" className={btn} onClick={close}>Keep them</button>
          <button type="button" className={danger} disabled={n === 0 && !clearAreas}
            onClick={() => {
              if (n > 0) remove(standing.filter((l) => ticked.has(l.id)).map((l) => l.id));
              if (clearAreas) act({ kind: 'area.clearAll' });
              close();
            }}>
            {n === 0 && clearAreas ? 'Clear the areas' : n === 1 ? 'Remove 1 token' : `Remove ${n} tokens`}
          </button>
        </div>
      </div>
    </Modal>
  );
}
